import { createContext, useContext } from "react";

export type Theme = "system" | "light" | "dark";
export const PRESETS = [
  { id: "mist", name: "晨雾睡莲", mode: "light" },
  { id: "garden", name: "日光花园", mode: "light" },
  { id: "wisteria", name: "紫藤微风", mode: "light" },
  { id: "jade", name: "青池", mode: "light" },
  { id: "night", name: "夜色鸢尾", mode: "dark" },
  { id: "dusk", name: "暮色玫瑰", mode: "dark" },
] as const;
export type Palette = typeof PRESETS[number]["id"];
export interface Appearance {
  theme: Theme;
  light: Palette;
  dark: Palette;
  artwork: boolean;
  edgeOpacity: number;
  reducedMotion?: boolean;
}
export const DEFAULT_APPEARANCE: Appearance = { theme: "system", light: "mist", dark: "night", artwork: true, edgeOpacity: 10, reducedMotion: false };
/** One browser preference for the Hub, every Agent and all same-origin tabs. */
export const APPEARANCE_KEY = "pa:appearance";
export function normalizeAppearance(value: unknown): Appearance {
  const raw: Record<string, unknown> = typeof value === "string" ? { theme: value } : value && typeof value === "object" ? value as Record<string, unknown> : {};
  const palette = (mode: "light" | "dark") => PRESETS.find(p => p.id === raw[mode] && p.mode === mode)?.id ?? DEFAULT_APPEARANCE[mode];
  return {
    theme: ["light", "dark", "system"].includes(String(raw.theme)) ? raw.theme as Theme : "system",
    light: palette("light"), dark: palette("dark"), artwork: raw.artwork !== false,
    reducedMotion: raw.reducedMotion === true,
    edgeOpacity: typeof raw.edgeOpacity === "number" && Number.isFinite(raw.edgeOpacity) ? Math.max(0, Math.min(20, raw.edgeOpacity)) : 10,
  };
}
export function readAppearance(): Appearance {
  try {
    const raw = window.localStorage.getItem(APPEARANCE_KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE };
    // Retain an existing light/dark preference when upgrading from mode-only.
    return normalizeAppearance(raw.startsWith("{") ? JSON.parse(raw) : raw);
  } catch { return { ...DEFAULT_APPEARANCE }; }
}
export function writeAppearance(value: Appearance): void {
  try { window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(normalizeAppearance(value))); } catch { /* Still applies in this document. */ }
}
export function effectiveTheme(): "light" | "dark" {
  const chosen = document.documentElement.dataset.theme;
  return chosen === "light" || chosen === "dark" ? chosen : window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
export function applyTheme(theme: Theme): void {
  if (theme === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}
export function applyAppearance(value: Appearance): void {
  const root = document.documentElement;
  const appearance = normalizeAppearance(value);
  applyTheme(appearance.theme);
  const palette = appearance[effectiveTheme()];
  root.dataset.palette = palette;
  root.dataset.artwork = String(appearance.artwork);
  root.dataset.motion = appearance.reducedMotion ? "reduced" : "full";
  root.style.setProperty("--owner-rail-texture", `url('/brand/${palette}-sidebar.webp')`);
  root.style.setProperty("--workspace-art", `url('/brand/${palette}-edge.webp')`);
  root.style.setProperty("--art-opacity", String(appearance.edgeOpacity / 100));
}
export const AppearanceContext = createContext<{ value: Appearance; onChange: (value: Appearance) => void }>({ value: DEFAULT_APPEARANCE, onChange: () => {} });
export const useAppearance = () => useContext(AppearanceContext);
